import { type ReactNode, useEffect, Suspense, Component, type ErrorInfo } from 'react'
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react'
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { isActivityPubHandle } from './lib/atExplore'
import { isChunkError, lazyWithRetry, CHUNK_RELOAD_KEY } from './lib/chunkRetry'
import { Layout } from './components/Layout'
import { MastodonCallback } from './pages/MastodonCallback'
import { PixelfedCallback } from './pages/PixelfedCallback'
import { useAuth } from './hooks/useAuth'
import { useFeatureFlags } from './hooks/useFeatureFlags'
import { usePageTracking } from './hooks/usePageTracking'
import { AdminRoute } from './components/AdminRoute'
import { ProtocolProvider } from './contexts/ProtocolContext'
import { CrossProtocolProvider } from './contexts/CrossProtocolContext/CrossProtocolContext'
import { AppViewProvider } from './contexts/AppViewContext'
import { FeatureFlagProvider } from './contexts/FeatureFlagContext'
import { VerificationPreferencesProvider } from './contexts/VerificationPreferencesContext'
import { PWAUpdatePrompt } from './components/PWAUpdatePrompt'
import { OfflineBanner } from './components/OfflineBanner'
import { InstallPrompt } from './components/InstallPrompt'
import { NostrReauthModal } from './components/NostrReauthModal'
import { Analytics } from '@vercel/analytics/react'

// Error boundary to handle chunk load failures after a new deploy
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
    constructor(props: { children: ReactNode }) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError() {
        return { hasError: true }
    }

    componentDidCatch(error: Error, _info: ErrorInfo) {
        // ChunkLoadError: a new deploy invalidated the old chunk hashes.
        // Auto-reload once — but only if we haven't already tried this session,
        // to prevent an infinite reload loop when the chunk is genuinely broken.
        if (isChunkError(error)) {
            if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
                sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
                window.location.reload()
            }
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark">
                    <div className="text-center p-8">
                        <p className="text-text-light dark:text-text-dark mb-4">
                            Something went wrong loading this page.
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="text-primary-light dark:text-primary-dark underline"
                        >
                            Reload
                        </button>
                    </div>
                </div>
            )
        }
        return this.props.children
    }
}

// Home is eagerly imported — it stays mounted (CSS keep-alive) whenever the user
// is authenticated, so lazy-loading it would cause a Suspense flash on first render
// that would replace every other route. All other pages remain lazy.
import { Home } from './pages/Home'

// Lazy-loaded route pages (not needed on initial load)
const Login = lazyWithRetry(() => import('./pages/Login').then(m => ({ default: m.Login })))
const Search = lazyWithRetry(() => import('./pages/Search').then(m => ({ default: m.Search })))
const Profile = lazyWithRetry(() => import('./pages/Profile').then(m => ({ default: m.Profile })))
const ViewProfile = lazyWithRetry(() =>
    import('./pages/ViewProfile').then(m => ({ default: m.ViewProfile }))
)
const SocialList = lazyWithRetry(() =>
    import('./pages/SocialList').then(m => ({ default: m.SocialList }))
)
const PostDetail = lazyWithRetry(() =>
    import('./pages/PostDetail').then(m => ({ default: m.PostDetail }))
)
const APPostDetail = lazyWithRetry(() =>
    import('./pages/APPostDetail').then(m => ({ default: m.APPostDetail }))
)
const Settings = lazyWithRetry(() =>
    import('./pages/Settings/Settings.component').then(m => ({ default: m.Settings }))
)
const AppearanceSettings = lazyWithRetry(() =>
    import('./pages/Settings/AppearanceSettings/AppearanceSettings.component').then(m => ({
        default: m.AppearanceSettings,
    }))
)
const ArticlesSettings = lazyWithRetry(() =>
    import('@/pages/Settings').then(m => ({ default: m.ArticlesSettings }))
)
const SearchSettings = lazyWithRetry(() =>
    import('@/pages/Settings').then(m => ({ default: m.SearchSettings }))
)
const DataSourcesSettings = lazyWithRetry(() =>
    import('./pages/Settings/DataSourcesSettings/DataSourcesSettings.component').then(m => ({
        default: m.DataSourcesSettings,
    }))
)
const VerificationSettings = lazyWithRetry(() =>
    import('./pages/Settings/VerificationSettings/VerificationSettings.component').then(m => ({
        default: m.VerificationSettings,
    }))
)
const BlockedUsersSettings = lazyWithRetry(() =>
    import('./pages/Settings/BlockedUsersSettings/BlockedUsersSettings.component').then(m => ({
        default: m.BlockedUsersSettings,
    }))
)
const AboutSettings = lazyWithRetry(() =>
    import('./pages/Settings/AboutSettings/AboutSettings.component').then(m => ({
        default: m.AboutSettings,
    }))
)
const FeatureFlagsSettings = lazyWithRetry(() =>
    import('./pages/Settings/FeatureFlagsSettings/FeatureFlagsSettings.component').then(m => ({
        default: m.FeatureFlagsSettings,
    }))
)
const LabsSettings = lazyWithRetry(() =>
    import('./pages/Settings/LabsSettings/LabsSettings.component').then(m => ({
        default: m.LabsSettings,
    }))
)
const AiPreferencesSettings = lazyWithRetry(() =>
    import('./pages/Settings/AiPreferencesSettings').then(m => ({
        default: m.AiPreferencesSettings,
    }))
)
const RssFeedsSettings = lazyWithRetry(() =>
    import('./pages/Settings/RssFeedsSettings').then(m => ({
        default: m.RssFeedsSettings,
    }))
)
const NotificationsSettings = lazyWithRetry(() =>
    import('@/pages/Settings').then(m => ({ default: m.NotificationsSettings }))
)
const Feeds = lazyWithRetry(() => import('./pages/Feeds').then(m => ({ default: m.Feeds })))
const FeedView = lazyWithRetry(() =>
    import('./pages/FeedView').then(m => ({ default: m.FeedView }))
)
const FeedShareRedirect = lazyWithRetry(() =>
    import('./pages/FeedView').then(m => ({ default: m.FeedShareRedirect }))
)
const ListView = lazyWithRetry(() =>
    import('./pages/ListView').then(m => ({ default: m.ListView }))
)
const MastodonListView = lazyWithRetry(() =>
    import('./pages/MastodonListView').then(m => ({ default: m.MastodonListView }))
)
const ArticleDetail = lazyWithRetry(() =>
    import('./pages/ArticleDetail').then(m => ({ default: m.ArticleDetail }))
)
const Notifications = lazyWithRetry(() =>
    import('./pages/Notifications').then(m => ({ default: m.Notifications }))
)
const Articles = lazyWithRetry(() =>
    import('./pages/Articles').then(m => ({ default: m.Articles }))
)
const Hashtag = lazyWithRetry(() => import('./pages/Hashtag').then(m => ({ default: m.Hashtag })))
const StoryFeed = lazyWithRetry(() =>
    import('./pages/StoryFeed').then(m => ({ default: m.StoryFeed }))
)
const EngagementList = lazyWithRetry(() =>
    import('./pages/EngagementList').then(m => ({ default: m.EngagementList }))
)
const ShareTarget = lazyWithRetry(() =>
    import('./pages/ShareTarget').then(m => ({ default: m.ShareTarget }))
)
const NostrPostDetail = lazyWithRetry(() =>
    import('./pages/NostrPostDetail').then(m => ({ default: m.NostrPostDetail }))
)
const DiscoverPeople = lazyWithRetry(() =>
    import('./pages/DiscoverPeople').then(m => ({ default: m.DiscoverPeople }))
)
const DiscoverFeedsList = lazyWithRetry(() =>
    import('./pages/DiscoverFeedsList').then(m => ({ default: m.DiscoverFeedsList }))
)
const PublicationView = lazyWithRetry(() =>
    import('./pages/PublicationView').then(m => ({ default: m.PublicationView }))
)
const SubscriptionList = lazyWithRetry(() =>
    import('./pages/SubscriptionList/SubscriptionList.component').then(m => ({
        default: m.default,
    }))
)
const TahquamenonChat = lazyWithRetry(() =>
    import('./pages/Tahquamenon/TahquamenonChat.component').then(m => ({
        default: m.TahquamenonChat,
    }))
)

function LoadingScreen() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark">
            <div className="text-center">
                <img
                    src="/assets/logo.png"
                    alt="Loading"
                    className="w-16 h-16 mx-auto mb-4 animate-pulse brightness-0 dark:invert"
                />
            </div>
        </div>
    )
}

function ProtectedRoute({ children }: { children: ReactNode }) {
    const { isAuthenticated } = useAuth()
    const location = useLocation()

    if (!isAuthenticated) {
        const returnTo = encodeURIComponent(location.pathname + location.search)
        return <Navigate to={`/login?returnTo=${returnTo}`} replace />
    }

    return <>{children}</>
}

function RedirectToProfile() {
    const { identifier } = useParams()
    return <Navigate to={`/profile/${identifier}`} replace />
}

function RedirectATPost() {
    const { identifier, rkey } = useParams()
    return <Navigate to={`/profile/${identifier}/post/${rkey}`} replace />
}

function RedirectAPPost() {
    const { handle, postId } = useParams()
    return <Navigate to={`/profile/${handle}/post/${postId}`} replace />
}

function RedirectToSocialList({ suffix }: { suffix: 'followers' | 'following' }) {
    const { identifier } = useParams()
    return <Navigate to={`/profile/${identifier}/${suffix}`} replace />
}

function RedirectToArticleDetail() {
    const { did, rkey } = useParams()
    if (!did || !rkey) return <Navigate to="/" replace />
    return <Navigate to={`/profile/${did}/article/${rkey}`} replace />
}

function PostDetailRouter() {
    const { identifier, postId } = useParams()

    useEffect(() => {
        window.scrollTo({ top: 0 })
    }, [identifier, postId])

    if (identifier && isActivityPubHandle(identifier)) {
        return <APPostDetail />
    }
    return <PostDetail />
}

function AppRoutes() {
    const { isAuthenticated } = useAuth()
    const {
        flags: { subscriptions: subscriptionsEnabled, tahquamenon: tahquamenonEnabled },
    } = useFeatureFlags()
    const { pathname } = useLocation()
    const isHome = pathname === '/'

    return (
        <Layout>
            <ChunkErrorBoundary>
                {/* CSS keep-alive: Home stays mounted (display:none when away) so the
                    virtualizer and scroll position are preserved across navigation.
                    Kept outside Suspense so lazy-loading other route chunks never
                    unmounts it. isVisible=false disables the virtualizer while hidden,
                    preventing ResizeObserver-driven size mutations that corrupt the
                    item cache. Requires eager import (not lazy) for the same reason. */}
                {isAuthenticated && (
                    <div style={{ display: isHome ? 'block' : 'none' }}>
                        <Home isVisible={isHome} />
                    </div>
                )}
                <Suspense fallback={<LoadingScreen />}>
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/mastodon/callback" element={<MastodonCallback />} />
                        <Route path="/pixelfed/callback" element={<PixelfedCallback />} />
                        <Route path="/share-target" element={<ShareTarget />} />
                        <Route
                            path="/"
                            element={isAuthenticated ? null : <Navigate to="/login" replace />}
                        />
                        <Route
                            path="/search"
                            element={
                                <ProtectedRoute>
                                    <Search />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/notifications"
                            element={
                                <ProtectedRoute>
                                    <Notifications />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/articles"
                            element={
                                <ProtectedRoute>
                                    <Articles />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/hashtag/:tag"
                            element={
                                <ProtectedRoute>
                                    <Hashtag />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/story"
                            element={
                                <ProtectedRoute>
                                    <StoryFeed />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/profile/:identifier/followers"
                            element={
                                <ProtectedRoute>
                                    <SocialList type="followers" />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/profile/:identifier/following"
                            element={
                                <ProtectedRoute>
                                    <SocialList type="following" />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/discover/people"
                            element={
                                <ProtectedRoute>
                                    <DiscoverPeople />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/discover/feeds"
                            element={
                                <ProtectedRoute>
                                    <DiscoverFeedsList />
                                </ProtectedRoute>
                            }
                        />
                        {/* Feed route must precede article route: both score equally in
                            React Router's ranking (static/param/static/param = 24); definition
                            order is the tiebreaker. Feed requires auth, article is public. */}
                        <Route
                            path="/profile/:identifier/feed/:rkey"
                            element={
                                <ProtectedRoute>
                                    <FeedShareRedirect />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/profile/:did/article/:rkey" element={<ArticleDetail />} />
                        <Route path="/publication/:did/:rkey" element={<PublicationView />} />
                        {subscriptionsEnabled && (
                            <Route
                                path="/profile/subscriptions"
                                element={
                                    <ProtectedRoute>
                                        <SubscriptionList />
                                    </ProtectedRoute>
                                }
                            />
                        )}
                        {tahquamenonEnabled && (
                            <>
                                <Route
                                    path="/tahquamenon"
                                    element={<Navigate to="/tahquamenon/chat" replace />}
                                />
                                <Route
                                    path="/tahquamenon/chat"
                                    element={
                                        <ProtectedRoute>
                                            <TahquamenonChat />
                                        </ProtectedRoute>
                                    }
                                />
                            </>
                        )}
                        <Route path="/profile/:identifier" element={<ViewProfile />} />
                        <Route
                            path="/profile/:identifier/post/:postId/likes"
                            element={
                                <ProtectedRoute>
                                    <EngagementList type="likes" />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/profile/:identifier/post/:postId/reposts"
                            element={
                                <ProtectedRoute>
                                    <EngagementList type="reposts" />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/profile/:identifier/post/:postId/quotes"
                            element={
                                <ProtectedRoute>
                                    <EngagementList type="quotes" />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/profile/:identifier/post/:postId"
                            element={<PostDetailRouter />}
                        />
                        <Route
                            path="/profile"
                            element={
                                <ProtectedRoute>
                                    <Profile />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings"
                            element={
                                <ProtectedRoute>
                                    <Settings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/appearance"
                            element={
                                <ProtectedRoute>
                                    <AppearanceSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/articles"
                            element={
                                <ProtectedRoute>
                                    <ArticlesSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/search"
                            element={
                                <ProtectedRoute>
                                    <SearchSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/data-sources"
                            element={
                                <ProtectedRoute>
                                    <DataSourcesSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/verification"
                            element={
                                <ProtectedRoute>
                                    <VerificationSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/ai-preferences"
                            element={
                                <ProtectedRoute>
                                    <AiPreferencesSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/rss-feeds"
                            element={
                                <ProtectedRoute>
                                    <RssFeedsSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/moderation"
                            element={<Navigate to="/settings/moderation/blocked-users" replace />}
                        />
                        <Route
                            path="/settings/moderation/blocked-users"
                            element={
                                <ProtectedRoute>
                                    <BlockedUsersSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/about"
                            element={
                                <ProtectedRoute>
                                    <AboutSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/labs"
                            element={
                                <ProtectedRoute>
                                    <LabsSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/notifications"
                            element={
                                <ProtectedRoute>
                                    <NotificationsSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/feature-flags"
                            element={
                                <AdminRoute>
                                    <FeatureFlagsSettings />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/feeds"
                            element={
                                <ProtectedRoute>
                                    <Feeds />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/feed/:feedUri"
                            element={
                                <ProtectedRoute>
                                    <FeedView />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/list/:listUri"
                            element={
                                <ProtectedRoute>
                                    <ListView />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/mastodon-list/:listId"
                            element={
                                <ProtectedRoute>
                                    <MastodonListView />
                                </ProtectedRoute>
                            }
                        />
                        {/* Legacy route redirects */}
                        <Route
                            path="/profile/article/:did/:rkey"
                            element={<RedirectToArticleDetail />}
                        />
                        <Route
                            path="/at/:identifier/followers"
                            element={
                                <ProtectedRoute>
                                    <RedirectToSocialList suffix="followers" />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/at/:identifier/following"
                            element={
                                <ProtectedRoute>
                                    <RedirectToSocialList suffix="following" />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/at/:identifier/post/:rkey" element={<RedirectATPost />} />
                        <Route path="/ap/:handle/post/:postId" element={<RedirectAPPost />} />
                        <Route
                            path="/nostr/event/:id"
                            element={
                                <ProtectedRoute>
                                    <NostrPostDetail />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/at/:identifier" element={<RedirectToProfile />} />
                        <Route path="/ap/:identifier" element={<RedirectToProfile />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </Suspense>
            </ChunkErrorBoundary>
        </Layout>
    )
}

function App() {
    const { isLoading } = useAuth()
    usePageTracking()

    if (isLoading) {
        return <LoadingScreen />
    }

    // FeatureFlagProvider must be inside AuthProvider (calls useAuth), QueryClientProvider
    // (calls useQuery), and ProtocolProvider (calls usePlatform, to gate isAdmin on the
    // selected protocol — see #2951)
    // AppViewProvider must be inside QueryClientProvider (uses useQueryClient)
    return (
        <SentryErrorBoundary>
            <AppViewProvider>
                <VerificationPreferencesProvider>
                    <ProtocolProvider>
                        <FeatureFlagProvider>
                            <CrossProtocolProvider>
                                <OfflineBanner />
                                <PWAUpdatePrompt />
                                <InstallPrompt />
                                <NostrReauthModal />
                                <Analytics />
                                <AppRoutes />
                            </CrossProtocolProvider>
                        </FeatureFlagProvider>
                    </ProtocolProvider>
                </VerificationPreferencesProvider>
            </AppViewProvider>
        </SentryErrorBoundary>
    )
}

export default App
